/**
 * Cadastro completo do produto: conteúdo, fiscal, logística, fotos e atributos próprios.
 * Cada campo mostra de onde veio o valor (tela, integração, planilha) e, vazio, o que herda do catálogo central.
 * Um rascunho único para todas as abas; "Salvar" envia só o que mudou.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, ImagePlus, Link2, Trash2 } from 'lucide-react';
import type { AttributeDef, ContentFieldKey, ContentUpdateBody, FieldSourceInfo, MediaItem, ProductContent } from '@berytus/contracts';
import { api, dateTime, HttpError } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { can } from '@berytus/contracts';

export type EditorTab = 'cadastro' | 'fiscal' | 'logistica' | 'fotos' | 'atributos';

const SRC: Record<string, string> = { manual: 'editado na tela', integration: 'integração', sheet: 'planilha', api: 'API' };
const ORIGIN_DETAIL = [
  '0 · Nacional',
  '1 · Estrangeira, importação direta',
  '2 · Estrangeira, adquirida no mercado interno',
  '3 · Nacional, importado acima de 40% até 70%',
  '4 · Nacional, processos produtivos básicos',
  '5 · Nacional, importado até 40%',
  '6 · Estrangeira, importação direta sem similar (CAMEX)',
  '7 · Estrangeira, mercado interno sem similar (CAMEX)',
  '8 · Nacional, importado acima de 70%',
];
const ORIGIN_TYPE: Record<string, string> = { reseller: 'Revenda', imported: 'Importado', manufacturer: 'Fabricação própria' };
const WARRANTY: Record<string, string> = { seller: 'Do vendedor', factory: 'De fábrica', none: 'Sem garantia' };

type Draft = Partial<Record<ContentFieldKey, string>>;
const asText = (v: string | number | null | undefined) => (v == null ? '' : typeof v === 'number' ? String(v).replace('.', ',') : v);

function sourceLine(s: FieldSourceInfo | undefined) {
  if (!s) return null;
  return `${SRC[s.src] ?? s.src}${s.byName ? ` por ${s.byName}` : ''} · ${dateTime(s.at)}`;
}

export function ProductEditor({ productId, tab }: { productId: string; tab: EditorTab }) {
  const me = useMe();
  const qc = useQueryClient();
  const canEdit = can(me.tenant.access, 'catalog', 'write') && !me.impersonating;
  const q = useQuery({ queryKey: ['product-content', productId], queryFn: () => api<ProductContent>(`/catalog/products/${productId}/content`) });
  const [draft, setDraft] = useState<Draft>({});
  const [attrs, setAttrs] = useState<Record<string, string | boolean | null>>({});
  const [locs, setLocs] = useState<Record<string, string>>({});
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  useEffect(() => { setDraft({}); setAttrs({}); setLocs({}); }, [productId]);

  const c = q.data;
  const changes = useMemo((): ContentUpdateBody => {
    if (!c) return {};
    const b: ContentUpdateBody = {};
    for (const [k, v] of Object.entries(draft) as [ContentFieldKey, string][]) {
      if (v !== asText(c.values[k])) b[k] = v.trim() === '' ? null : v;
    }
    const a = Object.fromEntries(Object.entries(attrs).filter(([k, v]) => (v ?? '') !== (c.attributes[k] ?? '')));
    if (Object.keys(a).length) b.attributes = a as Record<string, string | boolean | null>;
    const l = c.locations.filter((x) => k(locs, x.storeCode) !== undefined && locs[x.storeCode] !== (x.location ?? '')).map((x) => ({ storeCode: x.storeCode, location: locs[x.storeCode]!.trim() || null }));
    if (l.length) b.locations = l;
    return b;
  }, [c, draft, attrs, locs]);
  const dirty = Object.keys(changes).length > 0;

  const save = useMutation({
    mutationFn: () => api<{ content: ProductContent }>(`/catalog/products/${productId}/content`, { method: 'PUT', json: changes }),
    onSuccess: (r) => {
      qc.setQueryData(['product-content', productId], r.content);
      qc.invalidateQueries({ queryKey: ['product', productId] });
      qc.invalidateQueries({ queryKey: ['products'] });
      setDraft({}); setAttrs({}); setLocs({}); setFieldError(null);
    },
    onError: (err) => {
      if (err instanceof HttpError && err.code === 'invalid_field') setFieldError({ field: err.field ?? '', message: err.message });
    },
  });

  if (q.isPending) return <Loading />;
  if (q.error || !c) return <ErrorBox error={q.error} />;

  const val = (key: ContentFieldKey) => draft[key] ?? asText(c.values[key]);
  const set = (key: ContentFieldKey) => (v: string) => setDraft((d) => ({ ...d, [key]: v }));
  const ctx: Ctx = { c, canEdit, val, set, fieldError };
  const pharmacy = me.tenant.vertical === 'pharmacy';

  return (
    <div className="stack-y" style={{ paddingBottom: dirty ? 72 : 0 }}>
      {c.curationStatus === 'pending_curation' && (
        <div className="notice warn small">Marcado como medicamento e fora do catálogo Berytus: aguardando a curadoria da Berytus (prazo de {c.medicineLock?.dueBusinessDays ?? 5} dias úteis). Enquanto isso, com a trava de medicamentos ativa, o item não publica.</div>
      )}
      {c.medicineLock && tab === 'cadastro' && <MedicineLockNotice c={c} productId={productId} />}
      {c.base && tab !== 'fotos' && (
        <div className="notice small">Kit: o cadastro é o do produto base <Link to={`/catalogo/${c.base.id}?aba=${tab}`}>{c.base.sku}</Link>; alterações aqui valem para ele.</div>
      )}

      {tab === 'cadastro' && (
        <section className="panel">
          <h2>Identificação e conteúdo</h2>
          <div className="form-grid">
            <F ctx={ctx} k="title" label="Título" wide inherit={c.central?.title}
              hint={c.channelTitle && c.channelTitle !== c.values.title ? <>Medicamento: nos canais e nas telas da Berytus vai com o nome do catálogo mestre, <b>{c.channelTitle}</b>.</> : undefined} />
            <F ctx={ctx} k="brand" label="Marca" inherit={c.central?.brand} />
            <F ctx={ctx} k="manufacturer" label="Fabricante" inherit={c.central?.manufacturer} />
            <F ctx={ctx} k="model" label="Modelo" />
            <F ctx={ctx} k="department" label="Departamento" />
            <F ctx={ctx} k="category" label="Categoria" />
            <F ctx={ctx} k="subcategory" label="Subcategoria" />
            <F ctx={ctx} k="description" label="Descrição longa" wide type="area" inherit={c.central?.description}
              hint="Só texto. Vazia, o anúncio usa a do catálogo central." />
            {pharmacy && <F ctx={ctx} k="anvisaCode" label="Registro ANVISA" inherit={c.central?.anvisaCode} />}
            {pharmacy && <F ctx={ctx} k="anvisaExemption" label="Motivo de isenção do registro" />}
          </div>
        </section>
      )}

      {tab === 'fiscal' && (
        <section className="panel">
          <h2>Dados fiscais</h2>
          <p className="small muted" style={{ marginTop: -6 }}>
            Opcional. Regime da empresa: <b>{c.taxRegime === 'simples' ? 'Simples Nacional (CSOSN)' : c.taxRegime === 'normal' ? 'Regime normal (CST)' : 'não informado'}</b>{' '}
            · <Link to="/empresa">alterar</Link>
          </p>
          <div className="form-grid">
            <F ctx={ctx} k="ncm" label="NCM" placeholder="8 dígitos" />
            <F ctx={ctx} k="cest" label="CEST" placeholder="7 dígitos" />
            <F ctx={ctx} k="taxSegment" label="Segmento" placeholder="ex.: 13 medicamentos" hint="Preenchido pelo CEST quando vazio." />
            <F ctx={ctx} k="taxSituation" label={c.taxRegime === 'normal' ? 'CST' : 'CSOSN'} placeholder={c.taxRegime === 'normal' ? '2 ou 3 dígitos' : 'ex.: 102'} />
            <F ctx={ctx} k="originType" label="Tipo de origem">
              <select className="select" value={val('originType')} disabled={!canEdit} onChange={(e) => set('originType')(e.target.value)}>
                <option value="">—</option>
                {Object.entries(ORIGIN_TYPE).map(([v, l]) => <option key={v} value={v}>{l} ({v})</option>)}
              </select>
            </F>
            <F ctx={ctx} k="originDetail" label="Detalhe da origem">
              <select className="select" value={val('originDetail')} disabled={!canEdit} onChange={(e) => set('originDetail')(e.target.value)}>
                <option value="">—</option>
                {ORIGIN_DETAIL.map((l, i) => <option key={i} value={String(i)}>{l}</option>)}
              </select>
            </F>
            <F ctx={ctx} k="fci" label="FCI" placeholder="exigida nas origens 3, 5 e 8" />
            <F ctx={ctx} k="exTipi" label="EX TIPI" />
            <F ctx={ctx} k="taxRuleId" label="Regra tributária (Tax Rule ID)" hint={<Link to="/configuracoes/regras-tributarias">Gerenciar regras</Link>}>
              <select className="select" value={val('taxRuleId')} disabled={!canEdit} onChange={(e) => set('taxRuleId')(e.target.value)}>
                <option value="">—</option>
                {c.taxRules.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </F>
            <F ctx={ctx} k="taxUnit" label="Unidade comercial" placeholder="UN, CX, KG…" />
            <F ctx={ctx} k="eanInvoice" label="EAN da nota" hint="Quando o EAN tributável é diferente do EAN de venda." />
          </div>
        </section>
      )}

      {tab === 'logistica' && (
        <>
          <section className="panel">
            <h2>Embalagem de envio</h2>
            <p className="small muted" style={{ marginTop: -6 }}>Peso e medidas do pacote: é o que o frete dos marketplaces usa.</p>
            <div className="form-grid">
              <F ctx={ctx} k="netWeightKg" label="Peso líquido (kg)" type="number" />
              <F ctx={ctx} k="grossWeightKg" label="Peso bruto (kg)" type="number" />
              <F ctx={ctx} k="heightCm" label="Altura (cm)" type="number" />
              <F ctx={ctx} k="widthCm" label="Largura (cm)" type="number" />
              <F ctx={ctx} k="lengthCm" label="Comprimento (cm)" type="number" />
            </div>
          </section>
          <section className="panel">
            <h2>Prazos e garantia</h2>
            <div className="form-grid">
              <F ctx={ctx} k="handlingDays" label="Prazo de manuseio (dias)" type="number" hint="Dias para despachar depois da venda." />
              <F ctx={ctx} k="manufacturingDays" label="Prazo de fabricação (dias)" type="number" hint="Produto sob encomenda." />
              <F ctx={ctx} k="warrantyMonths" label="Garantia (meses)" type="number" />
              <F ctx={ctx} k="warrantyType" label="Tipo de garantia">
                <select className="select" value={val('warrantyType')} disabled={!canEdit} onChange={(e) => set('warrantyType')(e.target.value)}>
                  <option value="">—</option>
                  {Object.entries(WARRANTY).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </F>
            </div>
          </section>
          <section className="panel">
            <h2>Localização no estoque</h2>
            {c.locations.length === 0 ? <p className="small muted" style={{ margin: 0 }}>Nenhuma loja cadastrada.</p> : (
              <div className="form-grid">
                {c.locations.map((l) => (
                  <div className="field" key={l.storeCode}>
                    <label htmlFor={`loc-${l.storeCode}`}>{l.storeName}</label>
                    <input className="input" id={`loc-${l.storeCode}`} disabled={!canEdit} placeholder="corredor, prateleira, posição"
                      value={locs[l.storeCode] ?? l.location ?? ''} onChange={(e) => setLocs((x) => ({ ...x, [l.storeCode]: e.target.value }))} />
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {tab === 'fotos' && <Photos c={c} canEdit={canEdit} />}

      {tab === 'atributos' && (
        <section className="panel">
          <h2>Atributos próprios</h2>
          {c.attributeDefs.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>Nenhum atributo criado. <Link to="/configuracoes/atributos">Criar atributos</Link> (ex.: MIP, Linha, Coleção).</p>
          ) : (
            <>
              <div className="form-grid">
                {c.attributeDefs.map((d) => (
                  <AttrField key={d.id} d={d} canEdit={canEdit} source={c.sources[`attr:${d.key}`]}
                    value={d.key in attrs ? attrs[d.key]! : (c.attributes[d.key] ?? null)}
                    error={fieldError?.field === `attr:${d.key}` ? fieldError.message : null}
                    onChange={(v) => setAttrs((a) => ({ ...a, [d.key]: v }))} />
                ))}
              </div>
              <p className="small muted" style={{ marginBottom: 0 }}><Link to="/configuracoes/atributos">Gerenciar atributos</Link></p>
            </>
          )}
        </section>
      )}

      {save.error && !fieldError && <ErrorBox error={save.error} />}
      {canEdit && dirty && (
        <div className="savebar" role="status">
          <span><b>{Object.keys(changes).length}</b> alteração(ões) não salvas{fieldError ? <> · <span style={{ color: 'var(--err)' }}>{fieldError.message}</span></> : ''}</span>
          <span style={{ flex: 1 }} />
          <button className="btn" type="button" onClick={() => { setDraft({}); setAttrs({}); setLocs({}); setFieldError(null); }}>Descartar</button>
          <button className="btn primary" type="button" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Salvando…' : 'Salvar'}</button>
        </div>
      )}
    </div>
  );
}

const k = (o: Record<string, string>, key: string) => o[key];

interface Ctx {
  c: ProductContent;
  canEdit: boolean;
  val: (k: ContentFieldKey) => string;
  set: (k: ContentFieldKey) => (v: string) => void;
  fieldError: { field: string; message: string } | null;
}

/** Campo ligado ao rascunho (componente estável: o foco não se perde ao digitar). */
function F({ ctx, ...p }: { ctx: Ctx; k: ContentFieldKey; label: string; inherit?: string | null; hint?: ReactNode; wide?: boolean; type?: 'text' | 'number' | 'area'; placeholder?: string; children?: ReactNode }) {
  // Medicamento do catálogo mestre: os dados de cadastro vêm do mestre (trava ativa: só leitura; observando: aviso).
  const lock = ctx.c.medicineLock && ctx.c.medicineLock.fields.includes(p.k) ? ctx.c.medicineLock.mode : null;
  const hint = lock === 'enforce' ? <><Tag tone="info">dado do catálogo mestre</Tag> {p.hint}</>
    : lock === 'observe' ? <><Tag tone="warn">será travado</Tag> dado do catálogo mestre quando a trava de medicamentos for ativada. {p.hint}</> : p.hint;
  return (
    <Field {...p} hint={hint} value={ctx.val(p.k)} onChange={ctx.set(p.k)} source={ctx.c.sources[p.k]} canEdit={ctx.canEdit && lock !== 'enforce'}
      error={ctx.fieldError?.field === p.k ? ctx.fieldError.message : null} warning={ctx.c.warnings.find((w) => w.field === p.k)?.message ?? null} />
  );
}

function Field({ k: key, label, value, onChange, source, inherit, hint, wide, type = 'text', placeholder, canEdit, error, warning, children }: {
  k: ContentFieldKey; label: string; value: string; onChange: (v: string) => void; source?: FieldSourceInfo; inherit?: string | null;
  hint?: ReactNode; wide?: boolean; type?: 'text' | 'number' | 'area'; placeholder?: string; canEdit: boolean;
  error: string | null; warning: string | null; children?: ReactNode;
}) {
  const id = `f-${key}`;
  const inherited = !value && inherit;
  return (
    <div className="field" style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <label htmlFor={id}>{label}</label>
      {children ?? (type === 'area' ? (
        <textarea className="input" id={id} rows={7} style={{ height: 'auto', padding: 10, resize: 'vertical' }} disabled={!canEdit}
          value={value} placeholder={inherit ?? placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className="input" id={id} disabled={!canEdit} inputMode={type === 'number' ? 'decimal' : undefined}
          value={value} placeholder={inherit ?? placeholder} onChange={(e) => onChange(e.target.value)} />
      ))}
      {error && <span className="small" style={{ color: 'var(--err)' }}>{error}</span>}
      {!error && warning && <span className="small" style={{ color: 'var(--warn)' }}>{warning}</span>}
      <span className="small muted">
        {inherited ? <><Tag tone="info">catálogo central</Tag> vazio aqui: vale o valor do catálogo central</> : sourceLine(source)}
        {value && inherit && canEdit && key !== 'title' && <>{source ? ' · ' : ''}<button type="button" className="linkish" onClick={() => onChange('')}>voltar para o central</button></>}
      </span>
      {hint && <span className="small muted">{hint}</span>}
    </div>
  );
}

function AttrField({ d, value, onChange, source, canEdit, error }: {
  d: AttributeDef; value: string | number | boolean | null; onChange: (v: string | boolean | null) => void;
  source?: FieldSourceInfo; canEdit: boolean; error: string | null;
}) {
  const id = `a-${d.key}`;
  return (
    <div className="field">
      <label htmlFor={id}>{d.label}{d.unit ? ` (${d.unit})` : ''} <span className="code" style={{ fontSize: 11 }}>{d.key}</span></label>
      {d.dataType === 'boolean' ? (
        <select className="select" id={id} disabled={!canEdit} value={value == null ? '' : value ? 'true' : 'false'}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}>
          <option value="">—</option><option value="true">Sim</option><option value="false">Não</option>
        </select>
      ) : d.dataType === 'enum' ? (
        <select className="select" id={id} disabled={!canEdit} value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">—</option>
          {d.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input className="input" id={id} disabled={!canEdit} type={d.dataType === 'date' ? 'date' : 'text'} inputMode={d.dataType === 'number' ? 'decimal' : undefined}
          value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value || null)} />
      )}
      {error && <span className="small" style={{ color: 'var(--err)' }}>{error}</span>}
      {source && <span className="small muted">{sourceLine(source)}</span>}
    </div>
  );
}

const toBase64 = (f: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
  r.onerror = () => reject(r.error);
  r.readAsDataURL(f);
});

function Photos({ c, canEdit }: { c: ProductContent; canEdit: boolean }) {
  const qc = useQueryClient();
  const gen = useMutation({
    mutationFn: () => api(`/catalog/products/${c.productId}/kit-image`, { method: 'POST' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['product-content', c.productId] }),
  });
  if (c.isKit) {
    return (
      <MediaGrid c={c} canEdit={canEdit} level="sku" items={c.media.sku} title={`Fotos do kit (${c.kitUnits} unidades)`}
        empty={<>Sem fotos próprias. Gere a foto do kit a partir da capa do produto base, ou envie uma.</>}
        extra={canEdit && (
          <>
            <button className="btn primary" type="button" disabled={gen.isPending} onClick={() => gen.mutate()}>
              <ImagePlus size={16} /> {gen.isPending ? 'Gerando…' : `Gerar foto do kit (${c.kitUnits}×)`}
            </button>
            {gen.error && <ErrorBox error={gen.error} />}
          </>
        )}
        note="A foto gerada repete a capa do produto base em grade, sobre fundo branco, sem texto (os marketplaces recusam texto na capa). Ela entra como capa do kit." />
    );
  }
  return (
    <MediaGrid c={c} canEdit={canEdit} level="product" items={c.media.product} title="Fotos"
      empty={(c.central?.images.length ?? 0) > 0 ? (
        <>Sem fotos próprias: os anúncios usam as {c.central!.images.length} foto(s) do catálogo central.
          <div className="gallery">{c.central!.images.map((u) => <img key={u} src={u} alt="" loading="lazy" />)}</div></>
      ) : null} />
  );
}

function MediaGrid({ c, canEdit, level, items, title, empty, extra, note }: {
  c: ProductContent; canEdit: boolean; level: 'product' | 'sku'; items: MediaItem[]; title: string;
  empty?: ReactNode; extra?: ReactNode; note?: string;
}) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const refresh = () => qc.invalidateQueries({ queryKey: ['product-content', c.productId] });
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const errs: string[] = [];
      for (const f of files) {
        try {
          if (f.size > 10 * 1024 * 1024) throw new Error('maior que 10 MB');
          await api(`/catalog/products/${c.productId}/media`, { method: 'POST', json: { level, file: { name: f.name, contentType: f.type, base64: await toBase64(f) } } });
        } catch (err) {
          errs.push(`${f.name}: ${err instanceof Error ? err.message : 'falhou'}`);
        }
      }
      setErrors(errs);
    },
    onSettled: refresh,
  });
  const byUrl = useMutation({
    mutationFn: () => api(`/catalog/products/${c.productId}/media`, { method: 'POST', json: { level, url: url.trim() } }),
    onSuccess: () => { setUrl(''); refresh(); },
  });
  const order = useMutation({
    mutationFn: (ids: string[]) => api(`/catalog/products/${c.productId}/media/order`, { method: 'PUT', json: { level, ids } }),
    onSettled: refresh,
  });
  const del = useMutation({
    mutationFn: (m: MediaItem) => api(`/catalog/products/${c.productId}/media/${m.id}`, { method: 'DELETE' }),
    onSettled: refresh,
  });
  const move = (i: number, d: -1 | 1) => {
    const ids = items.map((m) => m.id);
    [ids[i], ids[i + d]] = [ids[i + d]!, ids[i]!];
    order.mutate(ids);
  };

  return (
    <section className="panel">
      <h2>{title}</h2>
      <p className="small muted" style={{ marginTop: -6 }}>
        A primeira é a capa. JPG, PNG ou WebP, até 10 MB e 12 fotos; o Mercado Livre recomenda 1200 px e fundo branco na capa.
      </p>
      {note && <p className="small muted">{note}</p>}
      {items.length === 0 && empty && <div className="notice small" style={{ marginBottom: 12 }}>{empty}</div>}
      {items.length > 0 && (
        <div className="photo-grid">
          {items.map((m, i) => (
            <figure key={m.id} className="photo">
              <img src={m.url} alt={`Foto ${i + 1}`} loading="lazy" />
              <figcaption>
                <span className="small">{i === 0 ? <b>Capa</b> : `${i + 1}ª`}{m.width ? ` · ${m.width}×${m.height}` : ''}</span>
                {m.warnings.map((w) => <span key={w} className="small" style={{ color: 'var(--warn)' }}>{w}</span>)}
                {canEdit && (
                  <span style={{ display: 'flex', gap: 2 }}>
                    <button className="icon-btn" type="button" disabled={i === 0 || order.isPending} onClick={() => move(i, -1)} aria-label="Mover para a esquerda"><ArrowLeft size={14} /></button>
                    <button className="icon-btn" type="button" disabled={i === items.length - 1 || order.isPending} onClick={() => move(i, 1)} aria-label="Mover para a direita"><ArrowRight size={14} /></button>
                    <span style={{ flex: 1 }} />
                    <button className="icon-btn" type="button" disabled={del.isPending} onClick={() => confirm('Excluir esta foto?') && del.mutate(m)} aria-label="Excluir foto"><Trash2 size={14} /></button>
                  </span>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 14 }}>
          {extra}
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
            onChange={(e) => { const f = [...(e.target.files ?? [])]; e.target.value = ''; if (f.length) upload.mutate(f); }} />
          <button className={`btn${extra ? '' : ' primary'}`} type="button" disabled={upload.isPending || items.length >= 12} onClick={() => input.current?.click()}>
            <ImagePlus size={16} /> {upload.isPending ? 'Enviando…' : 'Enviar fotos'}
          </button>
          <span className="small muted">ou</span>
          <input className="input" style={{ flex: '1 1 260px', maxWidth: 420 }} placeholder="https://… endereço de uma imagem" value={url} onChange={(e) => setUrl(e.target.value)} />
          <button className="btn" type="button" disabled={!/^https?:\/\//.test(url.trim()) || byUrl.isPending} onClick={() => byUrl.mutate()}>
            <Link2 size={16} /> {byUrl.isPending ? 'Baixando…' : 'Adicionar pela URL'}
          </button>
        </div>
      )}
      {(byUrl.error || del.error) && <div style={{ marginTop: 10 }}><ErrorBox error={byUrl.error ?? del.error} /></div>}
      {errors.length > 0 && <div className="notice err small" style={{ marginTop: 10 }}>{errors.map((e) => <div key={e}>{e}</div>)}</div>}
    </section>
  );
}

/** Medicamento do catálogo mestre: aviso da trava e "Contestar dado" (vai para a curadoria da Berytus). */
function MedicineLockNotice({ c, productId }: { c: ProductContent; productId: string }) {
  const lock = c.medicineLock!;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ field: 'title', suggestedValue: '', note: '', file: null as File | null });
  const send = useMutation({
    mutationFn: async () => {
      let attachment: { fileName: string; base64: string } | undefined;
      if (f.file) {
        if (f.file.size > 10 * 1024 * 1024) throw new Error('Anexo acima de 10 MB.');
        const b64 = await new Promise<string>((ok, bad) => { const r = new FileReader(); r.onload = () => ok(String(r.result).split(',')[1] ?? ''); r.onerror = bad; r.readAsDataURL(f.file!); });
        attachment = { fileName: f.file.name, base64: b64 };
      }
      return api<{ dueAt: string }>(`/catalog/products/${productId}/contest`, { method: 'POST', json: { field: f.field, suggestedValue: f.suggestedValue, note: f.note || undefined, attachment } });
    },
    onSuccess: () => { setOpen(false); setF({ field: 'title', suggestedValue: '', note: '', file: null }); },
  });
  const FIELDS: [string, string][] = [['title', 'Nome'], ['brand', 'Marca'], ['manufacturer', 'Fabricante'], ['anvisaCode', 'Registro Anvisa'], ['activeIngredients', 'Princípios ativos'],
    ['dosage', 'Concentração'], ['pharmaceuticalForm', 'Forma farmacêutica'], ['presentation', 'Apresentação'], ['tarja', 'Tarja'], ['description', 'Descrição'], ['image', 'Foto'], ['other', 'Outro']];
  return (
    <div className={`notice small${lock.mode === 'enforce' ? '' : ' warn'}`}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 260 }}>
          <b>Medicamento do catálogo Berytus</b> ({lock.master.title}). {lock.mode === 'enforce'
            ? 'Nome, marca, fabricante, modelo, descrição e registro vêm do mestre; aqui você altera preço, estoque e foto.'
            : 'Quando a trava de medicamentos for ativada, nome, marca, fabricante, modelo, descrição e registro passam a vir só do mestre.'}
          {' '}Viu um dado errado? Conteste: a curadoria responde em até {lock.dueBusinessDays} dias úteis.
        </span>
        <button type="button" className="btn sm" onClick={() => setOpen(!open)}>{open ? 'Fechar' : 'Contestar dado'}</button>
      </div>
      {send.isSuccess && <div style={{ marginTop: 6 }}>Contestação enviada. A curadoria responde em até {lock.dueBusinessDays} dias úteis.</div>}
      {open && (
        <form style={{ display: 'grid', gap: 8, marginTop: 10 }} onSubmit={(e) => { e.preventDefault(); send.mutate(); }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ display: 'grid', gap: 4 }}>Campo
              <select className="select" value={f.field} onChange={(e) => setF({ ...f, field: e.target.value })}>{FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
            <label style={{ display: 'grid', gap: 4, flex: 1, minWidth: 220 }}>Valor certo<input className="input" required value={f.suggestedValue} onChange={(e) => setF({ ...f, suggestedValue: e.target.value })} /></label>
          </div>
          <label style={{ display: 'grid', gap: 4 }}>Observação<input className="input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></label>
          <label style={{ display: 'grid', gap: 4 }}>Anexo (opcional: PDF, JPG ou PNG até 10 MB)<input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setF({ ...f, file: e.target.files?.[0] ?? null })} /></label>
          {send.error && <ErrorBox error={send.error} />}
          <div><button type="submit" className="btn primary sm" disabled={send.isPending}>Enviar para a curadoria</button></div>
        </form>
      )}
    </div>
  );
}
